import React, { useRef, useEffect } from 'react';
import type { ChartData } from '../../types/analytics';

interface Props {
  data: ChartData;
  title: string;
  height?: number;
  showGrid?: boolean;
  animate?: boolean;
}

export default function LineChart({ 
  data, 
  title, 
  height = 300, 
  showGrid = true, 
  animate = true 
}: Props) {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvasRef.current || !data.datasets.length) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Set canvas size
    const rect = canvas.getBoundingClientRect();
    canvas.width = rect.width * window.devicePixelRatio;
    canvas.height = height * window.devicePixelRatio;
    ctx.scale(window.devicePixelRatio, window.devicePixelRatio);

    // Clear canvas
    ctx.clearRect(0, 0, rect.width, height);

    const padding = 40;
    const chartWidth = rect.width - padding * 2;
    const chartHeight = height - padding * 2;

    const dataset = data.datasets[0];
    const values = dataset.data;
    const maxValue = Math.max(...values, 0);
    const minValue = Math.min(...values, 0);
    const valueRange = maxValue - minValue || 1;

    // Draw grid
    if (showGrid) {
      ctx.strokeStyle = 'rgba(75, 85, 99, 0.1)';
      ctx.lineWidth = 1;

      // Horizontal grid lines
      for (let i = 0; i <= 5; i++) {
        const y = padding + (chartHeight / 5) * i;
        ctx.beginPath();
        ctx.moveTo(padding, y);
        ctx.lineTo(padding + chartWidth, y);
        ctx.stroke();
      }

      // Vertical grid lines
      const stepX = chartWidth / (data.labels.length - 1 || 1);
      for (let i = 0; i < data.labels.length; i++) {
        const x = padding + stepX * i;
        ctx.beginPath();
        ctx.moveTo(x, padding);
        ctx.lineTo(x, padding + chartHeight);
        ctx.stroke();
      }
    }

    // Draw line
    if (values.length > 1) {
      ctx.strokeStyle = dataset.borderColor || '#8b5cf6';
      ctx.lineWidth = 3;
      ctx.lineCap = 'round';
      ctx.lineJoin = 'round';

      ctx.beginPath();
      const stepX = chartWidth / (values.length - 1);

      values.forEach((value, index) => {
        const x = padding + stepX * index;
        const y = padding + chartHeight - ((value - minValue) / valueRange) * chartHeight;

        if (index === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      });

      ctx.stroke();

      // Draw fill area
      if (dataset.fill && dataset.backgroundColor) {
        ctx.fillStyle = dataset.backgroundColor;
        ctx.lineTo(padding + chartWidth, padding + chartHeight);
        ctx.lineTo(padding, padding + chartHeight);
        ctx.closePath();
        ctx.fill();
      }

      // Draw points
      ctx.fillStyle = dataset.borderColor || '#8b5cf6';
      values.forEach((value, index) => {
        const x = padding + stepX * index;
        const y = padding + chartHeight - ((value - minValue) / valueRange) * chartHeight;

        ctx.beginPath();
        ctx.arc(x, y, 4, 0, Math.PI * 2);
        ctx.fill();
      });
    }

    // Draw labels
    ctx.fillStyle = 'rgba(75, 85, 99, 0.8)';
    ctx.font = '12px sans-serif';
    ctx.textAlign = 'center';

    data.labels.forEach((label, index) => {
      const stepX = chartWidth / (data.labels.length - 1 || 1);
      const x = padding + stepX * index;
      ctx.fillText(label, x, height - 10);
    });

    // Draw y-axis labels
    ctx.textAlign = 'right';
    for (let i = 0; i <= 5; i++) {
      const value = minValue + (valueRange / 5) * (5 - i);
      const y = padding + (chartHeight / 5) * i + 4;
      ctx.fillText(value.toFixed(0), padding - 10, y);
    }

  }, [data, height, showGrid]);

  return (
    <div className="card-pastel p-6">
      <h3 className="text-lg font-semibold text-gray-800 mb-4 font-poppins">{title}</h3>
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full"
          style={{ height: `${height}px` }}
        />
      </div>
    </div>
  );
}
